import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index: number) => {
    setActive((index + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const slide = heroSlides[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-background-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点内容轮播"
    >
      <div className="relative h-[560px] w-full sm:h-[600px] md:h-[660px]">
        {heroSlides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={item.image}
              alt={`${item.title} 焦点推荐大图`}
              title={`${item.title} - 午夜视频焦点推荐`}
              className={`h-full w-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/75 to-background-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/25 to-background-950/40" />
          </div>
        ))}

        <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-10 md:px-8 md:pb-14">
          <div className="animate-rise max-w-3xl" key={slide.id}>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-background-950/60 px-3 py-1.5 text-xs text-accent-400 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500 animate-soft-pulse" />
              {slide.label}
            </p>
            <h1 className="font-heading text-3xl font-bold leading-tight tracking-wide text-foreground-50 md:text-5xl">
              把深夜，留给好电影
            </h1>
            <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-2">
              <h2 className="font-heading text-xl font-semibold text-accent-400 md:text-2xl">
                {slide.title}
              </h2>
              <span className="text-xs text-secondary-300 md:text-sm">{slide.meta}</span>
            </div>
            <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-300 md:text-base">
              {slide.subtitle}
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={slide.href}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-base" />
                查看内容详情
              </Link>
              <Link
                to="/movies"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-500/40 bg-background-950/40 px-5 py-3 text-sm text-foreground-100 backdrop-blur-sm transition-colors hover:border-accent-500/70 hover:text-accent-400"
              >
                浏览全部午夜电影
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 right-4 z-20 flex items-center gap-3 md:bottom-14 md:right-8 md:gap-6">
          <div className="flex items-center gap-2">
            {heroSlides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`切换到第 ${index + 1} 个推荐`}
                aria-current={index === active}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === active ? "w-10 bg-accent-500" : "w-4 bg-foreground-600 hover:bg-foreground-400"
                }`}
              />
            ))}
          </div>
          <span className="hidden font-label text-sm tracking-[0.2em] text-secondary-300 md:inline">
            {String(active + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
          </span>
          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={() => goTo(active - 1)}
              aria-label="上一个推荐"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-secondary-500/40 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => goTo(active + 1)}
              aria-label="下一个推荐"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-secondary-500/40 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}